import { useSMANumbers } from '@core/logic/hooks/useSMANumbers';
import { organizationManagementApiV1 as omaApi } from '@deepup/apis';
import { SearchOutline, TrashcanOutline, TrenchPipesOutline } from '@deepup/icons';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { OrderNumberType, isOrderNumberClosed } from '@projects/types/orderNumbers';
import { viewer3dSelectors } from '@viewer3D/redux/selectors';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOrderNumbers } from './useOrderNumbers';

const createSMAGridColumns = ({
  scanSmaValues,
  selectedSmas,
  showInheritSelect,
  onInheritChange,
  inheritOrderNumbers,
}: {
  scanSmaValues: Set<string>;
  selectedSmas: string[];
  showInheritSelect: boolean;
  inheritOrderNumbers: Set<string>;
  onInheritChange: (orderNumber: string, checked: boolean) => void;
}): GridColDef<omaApi.OrderNumber>[] => [
  {
    field: 'assigned',
    headerName: '',
    width: 50,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams<omaApi.OrderNumber>) => {
      const orderNumber = params.row.orderNumber;
      const isAssigned = scanSmaValues.has(orderNumber);
      if (!isAssigned) {
        return null;
      }
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <TrenchPipesOutline fontSize="large" />
        </div>
      );
    },
  },
  {
    field: 'SMA',
    flex: 1,
    valueGetter: (_, row) => row.orderNumber,
    valueFormatter: (value, row) => {
      const closed = isOrderNumberClosed(row);
      return closed ? `${value} (Closed)` : value;
    },
  },
  {
    field: 'ASB',
    flex: 1,
    valueGetter: (_, { networkNodeMappings }) => networkNodeMappings.at(0)?.asb,
  },
  {
    field: 'HK Number',
    flex: 1,
    valueGetter: (_, { networkNodeMappings }) => networkNodeMappings.at(0)?.hkNumber,
  },
  {
    field: 'NVT Number',
    flex: 1,
    valueGetter: (_, { networkNodeMappings }) => networkNodeMappings.at(0)?.nvtNumber,
  },
  {
    field: 'ONKZ',
    flex: 1,
    valueGetter: (_, { networkNodeMappings }) => networkNodeMappings.at(0)?.onkzNumber,
  },
  {
    field: 'Hierarchy',
    flex: 1,
    valueGetter: (_, { networkNodeMappings }) =>
      omaApi.NetworkNodeType[networkNodeMappings.at(0)?.type ?? 'UNSPECIFIED'],
  },
  ...(showInheritSelect
    ? [
        {
          field: 'Inherit',
          flex: 1,
          renderCell: ({ row }: GridRenderCellParams<omaApi.OrderNumber>) => (
            <Checkbox
              disabled={!selectedSmas.includes(row.orderNumber)}
              onClick={(e) => e.stopPropagation()}
              checked={inheritOrderNumbers?.has(row.orderNumber)}
              onChange={(e) => {
                onInheritChange(row.orderNumber, e.target.checked);
              }}
            />
          ),
        },
      ]
    : []),
];

export const ElementSMAs = ({
  onDelete,
  onClose,
  submitButtonLabel = 'Update Element',
  onSubmitSmas,
  selectedSmas: preselectedSmas,
  setInheritOrderNumbers,
  inheritOrderNumbers = new Set<string>(),
}: {
  onDelete?: () => void;
  onClose: () => void;
  submitButtonLabel?: string;
  onSubmitSmas: (selectedSmas: string[]) => void;
  selectedSmas: string[];
  setInheritOrderNumbers?: Dispatch<SetStateAction<Set<string>>>;
  inheritOrderNumbers?: Set<string>;
}) => {
  const { orderNumbers, isLoading } = useOrderNumbers();
  const [searchQuery, setSearchQuery] = useState('');
  const [orderNumberType, setOrderNumberType] = useState(omaApi.NetworkNodeType['UNSPECIFIED']);
  const selectedScanId = useSelector(viewer3dSelectors.selectedScanId);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const { fetchSMANumbers } = useSMANumbers();
  const [scanSmaValues, setScanSmaValues] = useState<Set<string>>(new Set());
  const [selectedSmas, setSelectedSmas] = useState<GridRowSelectionModel>(preselectedSmas);
  const [filterBySelectedScan, setFilterBySelectedScan] = useState(false);

  useEffect(() => {
    setSelectedSmas(preselectedSmas);
  }, [preselectedSmas]);

  // Fetch SMAs for selected scan
  useEffect(() => {
    const fetchScanSMAs = async () => {
      if (!selectedScanId || !projectId) {
        setScanSmaValues(new Set());
        return;
      }

      try {
        const scanOrderNumbers = await fetchSMANumbers(projectId, selectedScanId, 'scans');
        // Extract SMA values (only SMAs, not other order number types)
        const smaValues = scanOrderNumbers
          .filter((on) => on.type === OrderNumberType.SMA)
          .map((on) => on.value);
        setScanSmaValues(new Set(smaValues));
      } catch (error) {
        console.error('Error fetching SMAs for scan:', error);
        setScanSmaValues(new Set());
      }
    };

    fetchScanSMAs();
  }, [selectedScanId, projectId, fetchSMANumbers]);

  const query = searchQuery.toLowerCase().trim();

  const filteredOrderNumbers = orderNumbers
    ?.filter((item) => {
      if (!query) return true;
      const smaMatch = item.orderNumber?.toLowerCase().includes(query);
      if (smaMatch) return true;

      const mappingMatch = item.networkNodeMappings?.some((mapping) => {
        if (mapping.name?.toLowerCase().includes(query)) return true;
        if (mapping.asb?.toLowerCase().includes(query)) return true;
        if (mapping.hkNumber?.toLowerCase().includes(query)) return true;
        if (mapping.nvtNumber?.toLowerCase().includes(query)) return true;
        if (mapping.popNumber?.toLowerCase().includes(query)) return true;
        if (mapping.bbNumber?.toLowerCase().includes(query)) return true;
        if (mapping.onkzNumber?.toLowerCase().includes(query)) return true;
        if (mapping.type !== undefined && mapping.type !== null) {
          const typeName = omaApi.NetworkNodeType[mapping.type];
          if (typeName?.toLowerCase().includes(query)) return true;
        }
        return false;
      });

      return Boolean(mappingMatch);
    })
    .filter(({ networkNodeMappings }) => {
      if (orderNumberType === omaApi.NetworkNodeType.UNSPECIFIED) {
        return true;
      }

      return networkNodeMappings.at(0)?.type === orderNumberType;
    })
    .filter(({ orderNumber }) => {
      if (!filterBySelectedScan) {
        return true;
      }

      return scanSmaValues.has(orderNumber);
    });

  const filteredOrderNumberMap = useMemo(() => {
    const map = new Map<string, omaApi.OrderNumber>();
    filteredOrderNumbers?.forEach((r) => map.set(r.orderNumber, r));
    return map;
  }, [filteredOrderNumbers]);

  const onInheritChange = (orderNumber: string, checked: boolean) => {
    if (setInheritOrderNumbers) {
      setInheritOrderNumbers((prev) => {
        const target = new Set([orderNumber]);

        return checked ? prev.union(target) : prev.difference(target);
      });
    }
  };

  const columns = createSMAGridColumns({
    scanSmaValues,
    selectedSmas: [...selectedSmas] as string[],
    showInheritSelect: !!setInheritOrderNumbers,
    onInheritChange,
    inheritOrderNumbers,
  });

  const handleSubmit = () => {
    const selectedSmaStrings = selectedSmas.map((sma) => String(sma));

    onSubmitSmas(selectedSmaStrings);
  };

  return (
    <>
      <DialogContent>
        <Stack gap={2} sx={{ flexGrow: 1, minHeight: 0 }}>
          <Typography>SMA number</Typography>
          <FormControlLabel
            control={
              <Switch
                value={filterBySelectedScan}
                onChange={(e) => setFilterBySelectedScan(e.target.checked)}
              />
            }
            label="Show SMAs used in selected scan"
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={orderNumberType}
            onChange={(_, value) => setOrderNumberType(value)}
          >
            <ToggleButton value={omaApi.NetworkNodeType.UNSPECIFIED}>All</ToggleButton>
            <ToggleButton value={omaApi.NetworkNodeType.HK}>HK</ToggleButton>
            <ToggleButton value={omaApi.NetworkNodeType.POP}>POP</ToggleButton>
            <ToggleButton value={omaApi.NetworkNodeType.NVT}>NVT</ToggleButton>
            <ToggleButton value={omaApi.NetworkNodeType.APL}>APL</ToggleButton>
            <ToggleButton value={omaApi.NetworkNodeType.BB}>BB</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutline />
                </InputAdornment>
              ),
            }}
          />
          <DataGridPro
            checkboxSelection
            keepNonExistentRowsSelected
            isRowSelectable={(params) => !isOrderNumberClosed(params.row)}
            getRowClassName={(params) => (isOrderNumberClosed(params.row) ? 'closed-sma-row' : '')}
            rows={filteredOrderNumbers ?? []}
            loading={isLoading}
            getRowId={(r) => r.orderNumber}
            columns={columns}
            rowSelectionModel={selectedSmas}
            onRowSelectionModelChange={(newSelection) => {
              if (!isLoading) {
                // Ensure no closed SMAs are in selection
                const validSelection = newSelection.filter((id) => {
                  const row = filteredOrderNumberMap.get(id as string);
                  return !isOrderNumberClosed(row);
                });
                setSelectedSmas(validSelection);
              }
            }}
            sx={{
              flexGrow: 1,
              '& .closed-sma-row': {
                color: 'text.disabled',
                opacity: 0.6,
                backgroundColor: 'action.hover',
              },
            }}
            hideFooter
          />
        </Stack>
      </DialogContent>
      <DialogActions disableSpacing>
        <Stack width="100%" textAlign="center" gap={2} p={2}>
          <Divider variant="fullWidth" />
          <Typography>Step 2 / 2</Typography>
          <Stack direction="row" gap={2} flexGrow={1}>
            {onDelete && (
              <Button
                variant="text"
                color="error"
                startIcon={<TrashcanOutline />}
                onClick={onDelete}
              >
                Delete element
              </Button>
            )}
            <Box component="div" flexGrow={1} />
            <Button onClick={onClose} variant="outlined" color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained">
              {submitButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </>
  );
};
